/**
 * Canonical event names for trace-web.
 *
 * Mirrors trace-back/src/tracking/catalog/events.ts and
 * trace-mobile/apps/native/src/Providers/Tracking/catalog/events.ts so all
 * three platforms emit interoperable event names to Amplitude / Braze /
 * AppsFlyer.
 *
 * When this list changes, sync with backend + mobile (or extract into a
 * shared monorepo package).
 */

export const TraceTokEvents = {
  // ---- TRACE+ canonical (domain.object.action) — kept for legacy dashboards
  FeedSessionOpened: 'feed.session.opened',
  FeedSessionClosed: 'feed.session.closed',
  ContentPlaybackStarted: 'content.playback.started',
  ContentPlaybackPaused: 'content.playback.paused',
  ContentPlaybackResumed: 'content.playback.resumed',
  ContentEngagementLiked: 'content.engagement.liked',
  ContentEngagementBookmarked: 'content.engagement.bookmarked',
  ContentEngagementShared: 'content.engagement.shared',
  PaywallViewShown: 'paywall.view.shown',
  PaywallViewDismissed: 'paywall.view.dismissed',
  PaywallPlanSelected: 'paywall.plan.selected',
  SubscriptionPurchaseInitiated: 'subscription.purchase.initiated',
  SubscriptionPurchaseFailed: 'subscription.purchase.failed',

  // ---- Account (P1)
  AccountCreateStarted: 'account_create_started',
  AccountCreated: 'account_created',
  AccountUpdated: 'account_updated',
  AccountVerified: 'account_verified',
  AccountInfoSubmitted: 'account_info_submitted',
  AccountLoggedIn: 'logged_in',
  AccountLoggedOut: 'logged_out',
  // Visible, countable companion to Amplitude's reserved $identify (which sets
  // user properties but is not shown as a normal event). Emitted alongside the
  // real identify() call on login so QA/analysts see an "identify" in the stream.
  Identify: 'identify',

  // ---- Attribution (P1, backend-emitted on web — listed for type safety)
  AppInstallAttributed: 'app_install_attributed',
  RegistrationAttributed: 'registration_attributed',
  SubscriptionAttributed: 'subscription_attributed',

  // ---- Content (P1 + P2)
  ContentImpression: 'content_impression',
  ContentStarted: 'content_started',
  ContentCompleted: 'content_completed',
  ContentShared: 'content_shared',
  WatchProgress: 'watch_progress',
  ContentDetailViewed: 'content_detail_viewed',
  ContentLoadTime: 'content_load_time',
  PlayerClosed: 'player_closed',
  ContentPaused: 'content_paused', // P3
  ContentResumed: 'content_resumed', // P3
  ResolutionChanged: 'resolution_changed', // P2

  // ---- Downloads (P2)
  ContentDownloadStarted: 'content_download_started',
  ContentDownloadFinished: 'content_download_finished',
  ContentDownloadCleared: 'content_download_cleared',

  // ---- Engagement (P1)
  BookmarkAdded: 'bookmark_added',
  BookmarkRemoved: 'bookmark_removed', // P2
  CreatorFollowed: 'creator_followed',
  CreatorUnfollowed: 'creator_unfollowed', // P2
  InterestsSelected: 'interests_selected', // P3
  LanguageChanged: 'language_changed', // P2

  // ---- Discovery / Search (P1, P2)
  SearchStarted: 'search_started', // P2
  SearchCompleted: 'search_completed',
  SearchResultClicked: 'search_result_clicked',

  // ---- CRM (P1) — usually backend-emitted from Braze webhook, listed for type safety
  EmailClicked: 'email_clicked',
  EmailUnsubscribed: 'email_unsubscribed',
  NotificationOpened: 'notification_opened',
  PushOpened: 'push_opened',

  // ---- Learning (P1)
  CourseStarted: 'course_started',
  CourseFinished: 'course_finished',
  UnitStarted: 'unit_started',
  UnitFinished: 'unit_finished',
  JobTrainingCompleted: 'job_training_completed',
  ExamStarted: 'exam_started',
  ExamEnded: 'exam_ended',
  CertificateDownloaded: 'certificate_downloaded',

  // ---- Navigation (P1, P2)
  CTAClicked: 'cta_clicked',
  PageViewed: 'page_viewed',
  DeeplinkOpened: 'deeplink_opened',
  MenuClicked: 'menu_clicked', // P2

  // ---- Subscription (P1, P2)
  GoToSubscription: 'go_to_subscription',
  PaywallViewed: 'paywall_viewed',
  SubscriptionCheckoutStarted: 'subscription_checkout_started',
  SubscriptionStarted: 'subscription_started',
  SubscriptionRenewed: 'subscription_renewed',
  SubscriptionCancelled: 'subscription_cancelled',
  SubscriptionPaused: 'subscription_paused',
  SubscriptionResumed: 'subscription_resumed',
  SubscriptionTermUpdated: 'subscription_term_updated',
  TrialStarted: 'trial_started',
  TrialEnded: 'trial_ended',
  TrialConverted: 'trial_converted',

  // ---- UGC (P1)
  UgcUploadStarted: 'ugc_upload_started',
  UgcUploadFinished: 'ugc_upload_finished',

  // ---- Interactive (P2)
  QuizStarted: 'quiz_started',
  QuizCompleted: 'quiz_completed',
  QuizResultsViewed: 'quiz_results_viewed',

  // ---- Technical (P2)
  ErrorEncountered: 'error_encountered',
} as const;

export type TraceTokEventName =
  (typeof TraceTokEvents)[keyof typeof TraceTokEvents];

/**
 * Events the web client should emit (source of truth: client).
 * Mirrors trace-back FRONTEND_EVENTS — keep in sync.
 */
export const FRONTEND_EVENTS = [
  'account_create_started',
  'content_completed',
  'content_impression',
  'content_shared',
  'content_started',
  'watch_progress',
  'bookmark_added',
  'creator_followed',
  'cta_clicked',
  'page_viewed',
  'deeplink_opened',
  'go_to_subscription',
  'paywall_viewed',
  'search_completed',
  'search_result_clicked',
  'notification_opened',
  'account_info_submitted',
  'menu_clicked',
  'error_encountered',
  'identify',
] as const;

/**
 * Events backend is source-of-truth for. The web client must NOT emit
 * these directly — backend fires them via webhooks / service writes.
 * Listed here for type safety + filtering in adapters.
 */
export const BACKEND_EVENTS = [
  'account_created',
  'logged_in',
  'logged_out',
  'app_install_attributed',
  'registration_attributed',
  'subscription_attributed',
  'subscription_cancelled',
  'subscription_renewed',
  'subscription_started',
  'trial_converted',
  'trial_ended',
  'trial_started',
  'subscription_paused',
  'subscription_term_updated',
  'course_started',
  'course_finished',
  'unit_started',
  'unit_finished',
  'job_training_completed',
  'exam_started',
  'exam_ended',
  'certificate_downloaded',
  'ugc_upload_started',
  'ugc_upload_finished',
  'push_opened',
  'email_clicked',
  'email_unsubscribed',
] as const;

export type FrontendEventName = (typeof FRONTEND_EVENTS)[number];
export type BackendEventName = (typeof BACKEND_EVENTS)[number];

/**
 * Emptied: the web client now emits a SINGLE canonical snake_case name per event
 * — no in-code dual-emission. Legacy dot-notation continuity is handled at the
 * analytics layer (Amplitude Transformations), driven by the events.registry
 * `replaces` map. Kept as an exported empty map so existing imports/re-exports
 * still resolve.
 */
export const MIGRATION_ALIASES: Record<string, string> = {};

/**
 * Events that should be silently dropped by the Amplitude adapter (too
 * high volume — e.g. 1Hz heartbeats). Other adapters may still receive them.
 */
export const AMPLITUDE_DROP_LIST: ReadonlySet<string> = new Set([
  'player_tick',
  'feed_heartbeat',
]);

/**
 * Per-event routing: which destinations should receive each event.
 * Default (if missing from this map) = Amplitude only.
 *
 * Mirrors trace-back TrackingConfigService routing matrix.
 */
export type VendorName = 'amplitude' | 'braze' | 'appsflyer' | 'backendRelay';

export const EVENT_ROUTING: Record<string, ReadonlyArray<VendorName>> = {
  // ---- Attribution: Amplitude + AppsFlyer (no Braze — AF is source of truth)
  app_install_attributed: ['amplitude', 'appsflyer'],
  registration_attributed: ['amplitude', 'appsflyer'],
  subscription_attributed: ['amplitude', 'appsflyer'],

  // ---- Subscription intent (client emits): all three + relay
  paywall_viewed: ['amplitude', 'braze', 'backendRelay'],
  go_to_subscription: ['amplitude', 'braze', 'backendRelay'],
  subscription_checkout_started: [
    'amplitude',
    'braze',
    'appsflyer',
    'backendRelay',
  ],

  // ---- Account intent
  account_create_started: ['amplitude', 'braze', 'backendRelay'],
  account_info_submitted: ['amplitude', 'braze', 'backendRelay'],

  // ---- Content engagement: Amplitude primary
  content_impression: ['amplitude'],
  content_started: ['amplitude'],
  content_completed: ['amplitude', 'braze'],
  content_shared: ['amplitude', 'braze'],
  watch_progress: ['amplitude'],
  content_detail_viewed: ['amplitude'],
  content_paused: ['amplitude'],
  content_resumed: ['amplitude'],

  // ---- Bookmarks / follows: Amplitude + Braze (CRM signal)
  bookmark_added: ['amplitude', 'braze'],
  bookmark_removed: ['amplitude'],
  creator_followed: ['amplitude', 'braze'],
  creator_unfollowed: ['amplitude'],

  // ---- CRM events: Amplitude + Braze
  email_clicked: ['amplitude', 'braze'],
  push_opened: ['amplitude', 'braze'],
  notification_opened: ['amplitude', 'braze'],

  // ---- Navigation
  page_viewed: ['amplitude'],
  cta_clicked: ['amplitude'],
  menu_clicked: ['amplitude'],
  deeplink_opened: ['amplitude', 'appsflyer'],

  // ---- Search
  search_started: ['amplitude'],
  search_completed: ['amplitude'],
  search_result_clicked: ['amplitude'],

  // ---- Interactive
  quiz_started: ['amplitude'],
  quiz_completed: ['amplitude', 'braze'],
  interests_selected: ['amplitude', 'braze'],
  language_changed: ['amplitude'],

  // ---- Technical
  error_encountered: ['amplitude'],
};

/**
 * Resolve which vendors should receive a given event.
 * Falls back to Amplitude-only for unknown events.
 */
export function getVendorsForEvent(
  event: string,
): ReadonlyArray<VendorName> {
  return EVENT_ROUTING[event] ?? ['amplitude'];
}

/**
 * Returns true if the web client is allowed to emit this event (i.e. it's
 * not server-side source-of-truth). Backend-emitted events should be
 * blocked at the adapter layer to prevent double-counting.
 */
export function isClientEmittable(event: string): boolean {
  return !(BACKEND_EVENTS as readonly string[]).includes(event);
}
